import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Dimensioned from './Dimensioned';
import './mecharts.css';

export const BASE = '/mecharts';
export const MAIL = 'mailto:paul@exitdeath.com?subject=Mecharts';

const CRAWLER = (
  <>
    <rect x="34" y="56" width="92" height="28" rx="6" />
    <path d="M46 84 L32 106 M68 84 L60 106 M92 84 L100 106 M114 84 L128 106" />
    <path d="M30 106 h4 M58 106 h4 M98 106 h4 M126 106 h4" />
  </>
);

const MACHINES = [
  {
    n: '01', id: 'ma-01', designation: 'MA-01', name: 'Veilleuse', subtitle: 'A crawler that carries a light.',
    etym: 'veilleuse (fr.): a night-light; also, one who keeps watch.',
    vision: 'It walks slower than you do. At night, when the corridor is dark or you are reading in a dim room, it carries a small warm light on its back. It settles in a corner and switches on, and the room has a new light source. Then it moves, and the light moves with it, and the shadows of your furniture turn. Sometimes it goes behind a plant to play with the shadows. It will never blind you. You know it is there because the light in your space is moving and bending.',
    routine: ['Wakes at dusk.', 'Crosses the room at the pace of a very slow walk.', 'Picks a corner, settles, lights up.', 'Moves again. Plays with a shadow. Rests.'],
    plate: [['Designation', 'MA-01'], ['Name', 'Veilleuse'], ['Type', 'Legged crawler'], ['Carries', 'One dim light, warm'], ['Pace', 'Slower than a walk'], ['Active', 'At night'], ['Status', 'Concept']],
    view: (
      <>
        {CRAWLER}
        <path d="M80 56 v-8" />
        <circle cx="80" cy="38" r="10" />
        <circle className="mx-halo" cx="80" cy="38" r="16" strokeDasharray="2 6" stroke="var(--mx-blue)" />
      </>
    ),
  },
  {
    n: '02', id: 'ma-02', designation: 'MA-02', name: 'Serre', subtitle: 'A crawler that carries plants.',
    etym: 'serre (fr.): a greenhouse; also, a claw.',
    vision: 'Several small plants ride in a bed on its back. The bed is instrumented: moisture, nutrients, temperature, light, and the faint electrical activity of the plants themselves. The machine moves them into the light when they need it and out of direct sun when they don\'t. If it rains, it can go outside and let them drink. When a room gets cold, it leaves. There may be a station where you refill its water. Its only goal is the plants\' wellbeing. Every day you see a being move through your flat, and you know it is taking care of something alive.',
    routine: ['Reads the bed: moisture, light, temperature.', 'Finds the right patch of sun, or of shade.', 'Goes outside when it rains.', 'Comes back in before it gets cold.'],
    plate: [['Designation', 'MA-02'], ['Name', 'Serre'], ['Type', 'Legged crawler'], ['Carries', '3 to 5 small plants'], ['Senses', 'Moisture, nutrients, temperature, light, plant signals'], ['Objective', 'The plants\' wellbeing'], ['Status', 'Concept']],
    view: (
      <>
        {CRAWLER}
        <path d="M48 56 l3 -16 h16 l3 16 M74 56 l3 -16 h16 l3 16 M100 56 l3 -16 h16 l3 16" />
        <path d="M58 40 v-12 c0 -6 5 -9 9 -8 M85 40 v-16 c0 -5 -4 -8 -8 -7 M111 40 v-10 c0 -6 5 -9 9 -8" />
      </>
    ),
  },
  {
    n: '03', id: 'ma-03', designation: 'MA-03', name: 'Héliotrope', subtitle: 'A bonsai that never grows.',
    etym: 'héliotrope (fr.): a plant that turns toward the sun.',
    vision: 'A small tree whose leaves are solar panels. It does not grow; it is made entirely of actuators and joints, and it reconfigures itself into different tree shapes to catch the most light wherever you put it. The body is a clear, bendable material. Inside it, the cables that carry power from the leaves to the battery are coated with a heat-reactive layer, so you can see their color change when energy flows. Along the leaves, strips of LEDs come on very gently at night. It takes from the sun in the day and gives back after dark.',
    routine: ['Reads the light where it stands.', 'Rearranges its branches toward it.', 'Charges. The cables change color.', 'At night, glows back, gently.'],
    plate: [['Designation', 'MA-03'], ['Name', 'Héliotrope'], ['Type', 'Actuated tree'], ['Leaves', 'Solar panels'], ['Body', 'Clear, bendable'], ['Cables', 'Thermochromic, visible'], ['Night', 'LED along the leaves'], ['Status', 'Concept']],
    view: (
      <>
        <path d="M50 104 h60 l-5 12 h-50 z" />
        <path d="M80 104 C80 90 70 86 72 72 C74 60 90 60 86 44" />
        <path d="M74 78 C62 76 54 66 50 54 M82 60 C96 60 106 54 110 44 M86 44 C86 36 88 32 88 27" />
        <path d="M50 54 l-10.4 -6 v-12 l10.4 -6 l10.4 6 v12 z" />
        <path d="M110 44 l-10.4 -6 v-12 l10.4 -6 l10.4 6 v12 z" />
        <path d="M88 27 l-7.8 -4.5 v-9 l7.8 -4.5 l7.8 4.5 v9 z" />
        <path d="M84 100 C83 88 77 84 78 74" stroke="var(--mx-blue)" strokeWidth="1.5" strokeDasharray="1.5 4" />
      </>
    ),
  },
];

const NOTES = [
  'All machinery exposed. No covers, no shells.',
  'All fasteners visible.',
  'Nothing out of place. Every part finished to be looked at.',
  'No screens, no app, no voice. A mission, not an interface.',
  'Slow. Nothing moves faster than it needs to.',
  'The machines share a floor and sometimes work together.',
];

const SCREWS = [{ top: 10, left: 10 }, { top: 10, right: 10 }, { bottom: 10, left: 10 }, { bottom: 10, right: 10 }];

function Plate({ as: Tag = 'section', dark, className = '', children, ...rest }) {
  return (
    <Tag className={`mx-plate ${dark ? 'mx-plate-dark' : ''} ${className}`} {...rest}>
      {SCREWS.map((s, i) => <i key={i} className="mx-screw" style={s} aria-hidden="true" />)}
      {children}
    </Tag>
  );
}

// ISO 1302 surface-finish mark.
function Finish({ className = '' }) {
  return (
    <svg className={className} width="44" height="22" viewBox="0 0 44 22" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
      <path d="M1 10 L7 20 L15 2 H42" />
      <text x="17" y="8" className="mx-label" fill="currentColor" stroke="none" style={{ fontSize: 10, textTransform: 'none' }}>Ra 0.4</text>
    </svg>
  );
}

function Datum() {
  return (
    <svg className="mx-datum" width="14" height="30" viewBox="0 0 14 30" fill="none" stroke="var(--mx-blue)" aria-hidden="true">
      <polygon points="7,0.5 2,8 12,8" fill="var(--mx-blue)" stroke="none" />
      <line x1="7" y1="8" x2="7" y2="16" />
      <rect x="0.5" y="16.5" width="13" height="13" />
      <text x="7" y="26.2" textAnchor="middle" fill="var(--mx-blue)" stroke="none" style={{ fontSize: 9, fontWeight: 500 }}>A</text>
    </svg>
  );
}

function Numbered({ items }) {
  return (
    <ol className="mx-routine">
      {items.map((t, i) => (
        <li key={t}><span className="mx-label" style={{ color: 'var(--mx-blue)' }}>{i + 1}</span><span>{t}</span></li>
      ))}
    </ol>
  );
}

function Sheet({ m, i }) {
  return (
    <Plate id={m.id} className="mx-sheet">
      <div className="flex justify-between items-start gap-6">
        <div>
          <div className="mx-display" style={{ fontSize: 44 }}>{m.n}</div>
          <h3>{m.name}</h3>
          <p className="mx-sub">{m.subtitle}</p>
        </div>
        <p className="mx-label text-right whitespace-nowrap" style={{ color: 'var(--mx-ink-3)' }}>SHEET {i} / 3<br />{m.designation}</p>
      </div>
      <div className="mt-7 grid gap-10 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_280px]">
        <div>
          <div className="mx-view">
            <svg viewBox="0 0 160 120" preserveAspectRatio="xMidYMid meet" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {m.view}
            </svg>
            <span className="mx-label">VIEW A</span>
          </div>
          <Plate as="div" dark className="mx-nameplate">
            <dl>
              {m.plate.map(([k, v]) => <div key={k} className="mx-row"><dt className="mx-label">{k}</dt><dd className="mx-label">{v}</dd></div>)}
            </dl>
          </Plate>
        </div>
        <div>
          <p className="mx-etym">{m.etym}</p>
          <p className="mx-prose mt-3.5">{m.vision}</p>
        </div>
        <div className="lg:col-start-2 xl:col-start-auto">
          <p className="mx-label" style={{ color: 'var(--mx-ink-3)', marginBottom: 6 }}>ROUTINE</p>
          <Numbered items={m.routine} />
        </div>
      </div>
    </Plate>
  );
}

function Cell({ k, className = '', children }) {
  return (
    <div className={`mx-cell ${className}`}>
      <div className="mx-label" style={{ color: 'var(--mx-ink-3)' }}>{k}</div>
      <div className="mx-cell-v">{children}</div>
    </div>
  );
}

export default function Site() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = 'Mecharts — Robotics as art';
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div className="mx min-h-screen">
      <div className="relative z-[1]">
        <header className="mx-nav sticky top-0 z-50">
          <div className="mx-wrap h-full flex items-center justify-between">
            <Link to={BASE} className="mx-display" style={{ fontSize: 12, letterSpacing: '0.18em' }} aria-label="Mecharts, home">MECHARTS</Link>
            <nav className="flex gap-4 sm:gap-6 text-[13px] font-medium">
              <a href="#machines" className="mx-link">Machines</a>
              <a href="#method" className="mx-link">Method</a>
              <a href="#status" className="mx-link hidden sm:inline">Status</a>
              <a href="#contact" className="mx-link">Contact</a>
            </nav>
          </div>
        </header>

        <main className="mx-wrap pt-5 flex flex-col gap-5">
          <Plate className="mx-hero">
            <Dimensioned>
              <h1 className="mx-display mx-wordmark" aria-label="Mecharts">MECHARTS</h1>
            </Dimensioned>
            <Datum />
            <Finish className="mx-finish" />
            <div className="mt-11 max-w-[640px]">
              <p className="mx-statement">Robots built as art objects.</p>
              <p className="mx-lede">Mecharts makes slow machines that inhabit a room and keep to a mission of their own. One carries a light through the house at night. One walks a bed of plants to the sun. One is a tree that never grows. Everything exposed, everything machined, nothing out of place.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#machines" className="mx-btn">See the three machines</a>
              <a href={MAIL} className="mx-btn mx-btn-ghost">Write to the workshop</a>
            </div>
            <div className="mx-strip mx-label">
              <span>ROBOTICS AS ART</span>
              <span>COVER SHEET</span>
            </div>
          </Plate>

          <div className="mx-intro">
            <h2 id="machines">Three machines</h2>
            <p>The first two are crawlers. The third is a tree. Each one has a mission, and none of them has a screen.</p>
          </div>

          {MACHINES.map((m, i) => <Sheet key={m.id} m={m} i={i + 1} />)}

          <p className="mx-aside">They are meant to share a floor, do their own work, and sometimes do it together.</p>

          <Plate id="method" className="mx-sheet">
            <div className="grid gap-12 lg:grid-cols-2">
              <div>
                <h2>Made to be looked at</h2>
                <p className="mx-prose mt-3.5">The robots are built and thought of as art pieces. Exposed metal, exposed bolts, exposed machinery, and nothing out of place: pure metal, engineered and machined parts that are beautiful on their own, the way a well-made bicycle is.</p>
              </div>
              <div>
                <p className="mx-label" style={{ color: 'var(--mx-ink-3)', marginBottom: 6 }}>NOTES</p>
                <Numbered items={NOTES} />
              </div>
            </div>
            <div className="mx-finish-note mx-label">
              <Finish />
              <span>UNLESS NOTED</span>
            </div>
          </Plate>

          <Plate id="status" className="mx-sheet">
            <h2>Where it stands</h2>
            <p className="mx-prose mt-3.5">Three concepts, written down. Parts and drawings in progress. Models of each machine will appear on this page as they exist.</p>
            <div className="mx-rev" role="table" aria-label="Revisions">
              <div className="mx-rev-row mx-label" role="row" style={{ color: 'var(--mx-ink-3)' }}>
                <span role="columnheader">REV</span><span role="columnheader">DATE</span><span role="columnheader">DESCRIPTION</span>
              </div>
              <div className="mx-rev-row text-[14px]" role="row">
                <span role="cell">A</span><span role="cell">2026.10</span><span role="cell">Site published. Three concepts: MA-01, MA-02, MA-03.</span>
              </div>
            </div>
          </Plate>
        </main>

        <footer id="contact" className="mx-wrap mx-footer">
          <div className="mx-title grid grid-cols-2 md:grid-cols-7">
            <Cell k="TITLE" className="md:col-span-2">
              <span className="mx-display" style={{ fontSize: 13, letterSpacing: '0.14em' }}>MECHARTS</span>
              <div className="text-[12px] font-normal" style={{ color: 'var(--mx-ink-2)' }}>Robotics as art</div>
            </Cell>
            <Cell k="CONTACT"><a href={MAIL} className="mx-link">paul@exitdeath.com</a></Cell>
            <Cell k="PROJECT OF"><Link to="/projects" className="mx-link">Exit Death</Link></Cell>
            <Cell k="SCALE">1 : 1</Cell>
            <Cell k="REV">A</Cell>
            <Cell k="DATE">2026.10</Cell>
          </div>
          <div className="mt-3 flex flex-wrap justify-between gap-x-6 gap-y-1 mx-label" style={{ color: 'var(--mx-ink-3)' }}>
            <span>ALL DIMENSIONS IN PX. TOLERANCES ±0.5 UNLESS NOTED. SURFACE FINISH <span style={{ textTransform: 'none' }}>Ra</span> 0.4. DO NOT SCALE DRAWING.</span>
            <span>© MECHARTS</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
